import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import AppScrollArea from '../../components/common/AppScrollArea';
import { useBreakpoints } from '../../hooks/useBreakpoints';
import { socket } from '../../socket';
import { useSearchContext } from '../../providers/SearchProvider';

import DashboardHomeMobileShell from './DashboardHomeMobileShell.jsx';
import DashboardHomeVersionFooter from './DashboardHomeVersionFooter.jsx';
import DashboardBackToTopButton from './DashboardBackToTopButton.jsx';
import DashboardQuickLinksSidebar from './DashboardQuickLinksSidebar.jsx';
import DashboardLayoutGrid from './DashboardLayoutGrid.jsx';
import SearchResultsCard from './search-results/SearchResultsCard.jsx';
import useHomeDashboardLayout from './hooks/useHomeDashboardLayout.js';
import { hasPermission, PERM } from '../../utils/accessPermissions';
import { canEditHomeDashboardLayout } from '../../utils/dashboard/homeDashboardSectionAccess';

const QUICK_LINKS_COLLAPSED_STORAGE_KEY = 'dashboardQuickLinksCollapsed';
/** 已废弃：曾错误写入全屏宽度，挂载时清理避免旧数据干扰 */
const DEPRECATED_SIDEBAR_WIDTH_STORAGE_KEY = 'dashboardQuickLinksSidebarWidth';

const DashboardScreen = () => {
	const { userInfo } = useSelector((state) => state.auth);
	const { showSearchCard } = useSearchContext();
	const {
		layout,
		isEditing,
		isResetting,
		actionMessage,
		startEditing,
		finishEditing,
		reorderSection,
		toggleSectionHidden,
		restoreDefaultLayout,
	} = useHomeDashboardLayout(userInfo);
	const [quickLinksCollapsed, setQuickLinksCollapsed] = useState(() => {
		if (typeof window === 'undefined') {
			return false;
		}
		return window.localStorage.getItem(QUICK_LINKS_COLLAPSED_STORAGE_KEY) === 'true';
	});

	const toggleQuickLinks = useCallback(() => {
		setQuickLinksCollapsed((prev) => !prev);
	}, []);

	const { breakpoints } = useBreakpoints();
	const isMobile = breakpoints.down('md');
	const showQuickLinksMenu = hasPermission(userInfo, PERM.MENU_DASHBOARD_MENU);

	const onCustomizeHome = !isEditing && !showSearchCard && canEditHomeDashboardLayout(userInfo) ? startEditing : undefined;

	const mainContent = useMemo(
		() =>
			showSearchCard ? (
				<SearchResultsCard />
			) : (
				<DashboardLayoutGrid
					layout={layout}
					userInfo={userInfo}
					isEditing={isEditing}
					isResetting={isResetting}
					actionMessage={actionMessage}
					onFinishEditing={finishEditing}
					onReorder={reorderSection}
					onToggleHidden={toggleSectionHidden}
					onRestoreDefault={restoreDefaultLayout}
				/>
			),
		[
			actionMessage,
			finishEditing,
			isEditing,
			isResetting,
			layout,
			reorderSection,
			restoreDefaultLayout,
			showSearchCard,
			toggleSectionHidden,
			userInfo,
		],
	);

	useEffect(() => {
		if (!socket.connected) {
			socket.connect();
		}

		if (userInfo) {
			socket.emit('join dashboard', userInfo);
		}
	}, [userInfo]);

	useEffect(() => {
		if (typeof window !== 'undefined') {
			window.localStorage.removeItem(DEPRECATED_SIDEBAR_WIDTH_STORAGE_KEY);
		}
	}, []);

	useEffect(() => {
		if (typeof window === 'undefined') {
			return undefined;
		}

		window.localStorage.setItem(QUICK_LINKS_COLLAPSED_STORAGE_KEY, String(quickLinksCollapsed));

		return undefined;
	}, [quickLinksCollapsed]);

	if (isMobile) {
		return (
			<DashboardHomeMobileShell>
				<div className='dashboard-screen dashboard-screen--mobile'>
					<div className='dashboard-screen-mobile__page'>
						<div className='dashboard-main-mobile'>
							{mainContent}
							<DashboardHomeVersionFooter onCustomizeHome={onCustomizeHome} />
						</div>
					</div>
				</div>
				<DashboardBackToTopButton />
			</DashboardHomeMobileShell>
		);
	}

	/** L 形：通栏 Header（Logo+搜索）在壳层；本行仅侧栏 QuickLinks + 主区 */
	return (
		<>
			<Row
				className={classNames('g-3 dashboard-screen dashboard-screen--home-l', {
					'dashboard-screen--sidebar-collapsed': showQuickLinksMenu && quickLinksCollapsed,
				})}
			>
				{showQuickLinksMenu ? (
					<DashboardQuickLinksSidebar collapsed={quickLinksCollapsed} onToggleCollapse={toggleQuickLinks} />
				) : null}
				<Col className='min-w-0 dashboard-screen__main'>
					<AppScrollArea className='app-main-scroll dashboard-screen__main-scroll' maxHeight='100%' fillViewport>
						{/* 顶栏 absolute 叠层：占位后卡片可滚入 glass 下透 */}
						<div className='app-main-scroll-nav-spacer' aria-hidden='true' />
						{mainContent}
						<DashboardHomeVersionFooter onCustomizeHome={onCustomizeHome} />
						<div className='app-main-scroll-end-spacer' aria-hidden='true' />
					</AppScrollArea>
				</Col>
			</Row>
			{/* 勿放进 Row：.row > * 会强制 width:100% / gutter padding，打偏 fixed 右下角 */}
			<DashboardBackToTopButton />
		</>
	);
};

export default DashboardScreen;
